import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { DropdownMenu, Button } from '@3plearning/react-components';
import { t } from '@lingui/macro';
import DatePicker from '../date-picker';
import {
  LegendWrapper,
  ColorBlock,
  Title,
  Results,
  CheckboxWrapper,
  DateRange,
  ButtonWrapper,
  Clear
} from './styled';
import Checkbox from '../checkbox';
import { CHECKED, UNCHECKED } from '../checkbox/constants';
import { CLEAR_ASSIGNMENT_FILTERS } from '../../containers/course-assign/course-assign-types';
import { updateFiltersActionCreator } from '../../containers/course-assign/course-assign-actions';
import { isDefined } from '../../helpers/utils';

const Legend = (props) => {
  const { i18n, isVisible, onApply } = props;
  const SELECT_RANGE = i18n._(t('viewKey.selectDateRange')`Select Date Range`);
  const showResultsList = [
    { value: 'LA', label: i18n._(t('viewKey.latestAttempt')`Latest attempt`) },
    { value: 'AO', label: i18n._(t('viewKey.assignedOnly')`Assigned only`) }
  ];
  const [assigned, setAssigned] = useState(true);
  const [isZeroToFiftyChecked, setZeroToFiftyChecked] = useState(true);
  const [isFiftyToEightyFiveChecked, setFiftyToEightyFiveChecked] = useState(
    true
  );
  const [isOverEightyFiveChecked, setOverEightyFiveChecked] = useState(true);
  const [selectionResult, setSelectionResult] = useState({
    value: 'LA',
    label: i18n._(t('viewKey.latestAttempt')`Latest attempt`)
  });
  const dateObj = new Date();
  const currentYear = dateObj.getFullYear();
  const currentMonth = dateObj.getMonth() + 1;
  const currentDay = dateObj.getDate();
  const today = `${currentYear}-${currentMonth}-${currentDay}`;
  // Two years ago to one year in the future
  const rangeStartDate = `${currentYear - 2}-${currentMonth}-${currentDay}`;
  const rangeEndDate = `${currentYear + 1}-${currentMonth}-${currentDay}`;
  const [dateOptions, setDateOptions] = useState(undefined);
  const dispatch = useDispatch();

  const activeClass = useSelector(
    (state) => state.CourseAssignReducer.activeClass
  );

  const activeGroup = useSelector(
    (state) => state.CourseAssignReducer.activeGroup
  );

  const assignUnassignedButtonClicked = useSelector(
    (state) => state.CourseAssignReducer.shouldClearFilter
  );

  const clearFilter = () => {
    setDateOptions(undefined);
    setSelectionResult({
      value: 'LA',
      label: i18n._(t('viewKey.latestAttempt')`Latest attempt`)
    });
    setAssigned(true);
    setZeroToFiftyChecked(true);
    setFiftyToEightyFiveChecked(true);
    setOverEightyFiveChecked(true);
    dispatch({ type: CLEAR_ASSIGNMENT_FILTERS });
    onApply();
  };

  const memoizedCallback = useCallback(clearFilter, []);

  const lastSelectedClass = useRef(activeClass);
  const lastSelectedGroup = useRef(activeGroup);

  useEffect(() => {
    if (assignUnassignedButtonClicked) {
      memoizedCallback();
    }
  }, [memoizedCallback, assignUnassignedButtonClicked]);

  useEffect(() => {
    if (
      lastSelectedClass.current !== activeClass ||
      lastSelectedGroup.current !== activeGroup
    ) {
      memoizedCallback();
    }
    lastSelectedClass.current = activeClass;
    lastSelectedGroup.current = activeGroup;
  }, [memoizedCallback, activeClass, activeGroup]);

  if (!isVisible) return null;

  const onDateChangeHandler = (selectedRangeType, startDate, endDate) => {
    setDateOptions({ startDate, endDate });
  };

  const datePickerLabels = {
    [DatePicker.CUSTOM_DATE]: i18n._(t('viewKey.customDate')`Custom date`),
    [SELECT_RANGE]: i18n._(t('viewKey.selectDateRange')`Select Date Range`)
  };

  const datePickerProps = {
    academicYear: { startDate: rangeStartDate, endDate: rangeEndDate },
    currentDate: {
      startDate: isDefined(dateOptions) ? dateOptions.startDate : today,
      endDate: isDefined(dateOptions) ? dateOptions.endDate : today,
      type: isDefined(dateOptions) ? DatePicker.CUSTOM_DATE : SELECT_RANGE
    },
    weeklyRangeOnly: false,
    options: [SELECT_RANGE, DatePicker.CUSTOM_DATE],
    labels: datePickerLabels,
    onDateChange: onDateChangeHandler
  };

  const applyFilter = () => {
    dispatch(
      updateFiltersActionCreator({
        dateData: {
          dateRange: dateOptions
        },
        rangeData: {
          assigned,
          zeroToFifty: isZeroToFiftyChecked,
          fiftyToEightyFive: isFiftyToEightyFiveChecked,
          overEightyFive: isOverEightyFiveChecked
        },
        dropdownValue: {
          selectedOption: selectionResult.value
        }
      })
    );
    onApply();
  };

  return (
    <LegendWrapper>
      <DateRange>
        <Title className="headerText">
          {i18n._(t('viewKey.label.filter')`Key/Filter`)}
        </Title>
        <Title>
          {i18n._(t('viewKey.label.showCells')`Show cells within date range:`)}
        </Title>
        <DatePicker {...datePickerProps} labels={datePickerLabels} />
      </DateRange>
      <Clear onClick={() => setDateOptions(undefined)}>
        {i18n._(t('viewKey.clear')`clear`)}
      </Clear>
      <Results>
        <Title>{i18n._(t('viewKey.label.showResults')`Show results…`)}</Title>
        <span data-testid="showResultsDropdown">
          <DropdownMenu
            key="showResults"
            data-name="showResults"
            options={showResultsList}
            selectedOption={selectionResult || showResultsList[0]}
            onSelectOption={(option) => {
              setSelectionResult(option);
            }}
          />
        </span>
      </Results>
      <Title>
        <span className="label">
          {i18n._(t('viewKey.label.studentProgress')`Student Progress`)}
        </span>
      </Title>
      <span className="item">
        <CheckboxWrapper>
          <Checkbox
            id="assigned-checkbox"
            checkboxHandler={() => {
              setAssigned(!assigned);
            }}
            state={assigned ? CHECKED : UNCHECKED}
            className="listCheckbox"
          />
        </CheckboxWrapper>
        <ColorBlock color="#dae7d3" data-name="assigned" />
        <label htmlFor="assigned">
          {i18n._(t('viewKey.label.assigned')`Assigned`)}
        </label>
      </span>
      <span className="item">
        <CheckboxWrapper>
          <Checkbox
            id="zeroToFifty-checkbox"
            checkboxHandler={() => {
              setZeroToFiftyChecked(!isZeroToFiftyChecked);
            }}
            state={isZeroToFiftyChecked ? CHECKED : UNCHECKED}
          />
        </CheckboxWrapper>
        <ColorBlock color="#900012" data-name="progress_1" />
        <label htmlFor="progress_1">
          {i18n._(t('viewKey.label.0-50')`0 - 50% Correct`)}
        </label>
      </span>
      <span className="item">
        <CheckboxWrapper>
          <Checkbox
            id="fiftyToEightyFive-checkbox"
            checkboxHandler={() => {
              setFiftyToEightyFiveChecked(!isFiftyToEightyFiveChecked);
            }}
            state={isFiftyToEightyFiveChecked ? CHECKED : UNCHECKED}
          />
        </CheckboxWrapper>
        <ColorBlock color="#314eb5" data-name="progress_2" />
        <label htmlFor="progress_2">
          {i18n._(t('viewKey.label.50-85')`50 - 85% Correct`)}
        </label>
      </span>
      <span className="item">
        <CheckboxWrapper>
          <Checkbox
            id="overEightyFive-checkbox"
            checkboxHandler={() => {
              setOverEightyFiveChecked(!isOverEightyFiveChecked);
            }}
            state={isOverEightyFiveChecked ? CHECKED : UNCHECKED}
          />
        </CheckboxWrapper>
        <ColorBlock color="#4bda40" data-name="progress_3" />
        <label htmlFor="progress_3">
          {i18n._(t('viewKey.label.85-100')`85 - 100% Correct`)}
        </label>
      </span>
      <ButtonWrapper>
        <Button
          alignIcon="LEFT"
          onClick={clearFilter}
          text={i18n._(t('viewKey.label.clear')`Clear`)}
          secondary
          type={Button.LOZENGE}
        />
        <Button
          alignIcon="RIGHT"
          onClick={applyFilter}
          text={i18n._(t('viewKey.label.apply')`Apply`)}
          primary
          type={Button.LOZENGE}
        />
      </ButtonWrapper>
    </LegendWrapper>
  );
};

Legend.propTypes = {
  i18n: PropTypes.object,
  isVisible: PropTypes.bool,
  css: PropTypes.object,
  onApply: PropTypes.func
};

Legend.defaultProps = {
  isVisible: false
};

export default Legend;



// WEBPACK FOOTER //
// ./components/legend/index.js